import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const INDIA_PATH =
  "M170,10 L190,42 L214,70 L240,120 L268,142 L300,150 L332,132 L360,140 L348,168 L312,172 L296,196 L272,206 L262,224 L232,256 L208,302 L186,362 L156,416 L138,356 L118,300 L104,248 L84,212 L52,206 L36,176 L48,150 L96,96 L128,56 L150,30 Z";

const CITIES = [
  { name: "Delhi NCR", x: 186, y: 112, type: "Enterprise & Consumer Hub", color: "#00E676" },
  { name: "Mumbai", x: 98, y: 246, type: "Financial Capital", color: "#FF8800" },
  { name: "Pune", x: 114, y: 264, type: "Industrial & SaaS Lab", color: "#00B0FF" },
  { name: "Ahmedabad", x: 86, y: 190, type: "Manufacturing & Trade Corridor", color: "#00E676" },
  { name: "Hyderabad", x: 168, y: 286, type: "Innovation & Cloud Center — LaunchLab HQ", color: "#FF8800" },
  { name: "Bengaluru", x: 150, y: 332, type: "DeepTech & AI Core", color: "#00B0FF" },
  { name: "Chennai", x: 184, y: 346, type: "SaaS & Mobility Coast", color: "#00E676" },
  { name: "Kolkata", x: 268, y: 202, type: "Eastern Trade Gateway", color: "#FF8800" },
];

const CHAIN = ["IDEAS", "STARTUPS", "BUSINESSES", "JOBS", "ECONOMIC GROWTH"];

export function IndiaVision() {
  const [active, setActive] = useState(CITIES[4]);

  return (
    <section id="india" data-testid="india-vision-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,rgba(0,230,118,0.04),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
          >
            <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">06 — The Vision</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="india-headline">
              BUILDING THE NEXT GENERATION OF{" "}
              <span className="text-gradient-india">INDIAN BUSINESSES</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              India has enormous entrepreneurial potential. LaunchLab's vision is to
              encourage more people to become:
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["Entrepreneurs", "Business Builders", "Innovators", "Job Creators"].map((t, i) => (
                <span
                  key={t}
                  className="glass px-4 py-2 rounded-full text-sm font-medium text-white"
                  style={{ borderColor: ["#FF880066", "#00B0FF66", "#00E67666", "#FF880066"][i] }}
                >
                  {t}
                </span>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-3" data-testid="india-chain">
              {CHAIN.map((c, i) => (
                <div key={c} className="flex items-center gap-2">
                  <span
                    className={`font-mono-tech text-[11px] sm:text-xs tracking-[0.18em] ${
                      i === CHAIN.length - 1 ? "text-[#00E676] font-semibold" : "text-slate-300"
                    }`}
                  >
                    {c}
                  </span>
                  {i < CHAIN.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-[#FF8800]" />}
                </div>
              ))}
            </div>

            {active && (
              <div className="mt-10 glass rounded-xl p-5 inline-block" data-testid="india-city-info">
                <p className="font-display font-bold text-white text-lg">{active.name}</p>
                <p className="text-sm mt-1" style={{ color: active.color }}>{active.type}</p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="relative max-w-[440px] mx-auto w-full"
            data-testid="india-map"
          >
            <svg viewBox="0 0 400 440" className="w-full h-auto drop-shadow-[0_0_40px_rgba(0,176,255,0.12)]">
              <defs>
                <linearGradient id="indiaFill" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#FF8800" stopOpacity="0.14" />
                  <stop offset="50%" stopColor="#0D111D" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#00E676" stopOpacity="0.14" />
                </linearGradient>
              </defs>
              <path
                d={INDIA_PATH}
                fill="url(#indiaFill)"
                stroke="rgba(148,163,184,0.45)"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
              {CITIES.map((c) => (
                <g key={c.name}>
                  <line x1={c.x} y1={c.y} x2="168" y2="286" stroke={c.color} strokeOpacity="0.25" strokeWidth="0.8" className="flow-line" />
                </g>
              ))}
              {CITIES.map((c) => (
                <g
                  key={c.name}
                  onClick={() => setActive(c)}
                  className="cursor-pointer"
                  data-testid={`india-map-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <circle cx={c.x} cy={c.y} r="14" fill={c.color} opacity="0.12" />
                  <circle cx={c.x} cy={c.y} r="7" fill={c.color} opacity="0.25" className="animate-pulse-node" />
                  <circle cx={c.x} cy={c.y} r="3.4" fill={c.color} stroke="#06080E" strokeWidth="1" />
                  <text
                    x={c.x + 9}
                    y={c.y + 3.5}
                    fill={active?.name === c.name ? "#ffffff" : "#94a3b8"}
                    fontSize="10.5"
                    fontFamily="JetBrains Mono, monospace"
                    letterSpacing="0.5"
                  >
                    {c.name}
                  </text>
                </g>
              ))}
            </svg>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
